import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import Lenis from "lenis";
import { gsap, ScrollTrigger, prefersReducedMotion } from "../../lib/motion";

/**
 * Site-wide inertial smooth scroll (Lenis) driven on GSAP's single ticker so
 * scroll position, DOM reveals and any scrubbed animation share one time base
 * (no 1-frame jitter). Adds a golden top progress bar and scans [data-parallax]
 * elements for scroll-scrubbed depth. Skips entirely under prefers-reduced-motion.
 */
export const SmoothScroll = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    if (prefersReducedMotion()) return;

    const lenis = new Lenis({
      duration: 1.15,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      smoothWheel: true,
      syncTouch: false,
      touchMultiplier: 1.5,
      wheelMultiplier: 1,
    });
    window.__lenis = lenis;

    const bar = document.createElement("div");
    bar.setAttribute("data-testid", "scroll-progress");
    bar.style.cssText =
      "position:fixed;top:0;left:0;height:2px;width:0%;z-index:9999;pointer-events:none;" +
      "background:linear-gradient(90deg,#8F7539,#C9B79C);box-shadow:0 0 12px rgba(201,183,156,.55)";
    document.body.appendChild(bar);

    lenis.on("scroll", ({ progress }) => {
      ScrollTrigger.update();
      bar.style.width = `${(progress * 100).toFixed(2)}%`;
    });

    const raf = (time) => lenis.raf(time * 1000);
    gsap.ticker.add(raf);
    gsap.ticker.lagSmoothing(0);

    const onRefresh = () => lenis.resize();
    ScrollTrigger.addEventListener("refresh", onRefresh);

    return () => {
      ScrollTrigger.removeEventListener("refresh", onRefresh);
      gsap.ticker.remove(raf);
      lenis.destroy();
      bar.remove();
      window.__lenis = null;
    };
  }, []);

  // Per-route: reset scroll, wire parallax depth, refresh triggers after layout.
  useEffect(() => {
    if (window.__lenis) window.__lenis.scrollTo(0, { immediate: true });
    else window.scrollTo(0, 0);

    if (prefersReducedMotion()) return;

    const ctx = gsap.context(() => {
      gsap.utils.toArray("[data-parallax]").forEach((el) => {
        const speed = parseFloat(el.getAttribute("data-parallax")) || 0.18;
        gsap.set(el, { scale: 1.16, transformOrigin: "center center" });
        gsap.fromTo(
          el,
          { yPercent: -speed * 100 },
          {
            yPercent: speed * 100,
            ease: "none",
            scrollTrigger: {
              trigger: el.parentElement || el,
              start: "top bottom",
              end: "bottom top",
              scrub: true,
            },
          }
        );
      });
    });

    const t = setTimeout(() => ScrollTrigger.refresh(), 350);
    return () => {
      clearTimeout(t);
      ctx.revert();
    };
  }, [pathname]);

  return null;
};

export default SmoothScroll;
